import { combinationCount, secureShuffle } from './lottery'

export type WelfareGameId = 'ssq' | 'qlc' | 'fc3d' | 'kl8'
export type Fc3dMode = 'direct' | 'group3' | 'group6'
export type OtherGenerationMode = 'quick' | 'conditional' | 'dantuo' | 'multiple'

export type WelfareGame = {
  id: WelfareGameId
  name: string
  shortName: string
  note: string
  rule: string
}

export type OtherGameTicket = {
  game: Exclude<WelfareGameId, 'ssq'>
  numbers: number[]
}

export type OtherNumberMetrics = {
  sum: number
  span: number
  odd: number
  even: number
  big: number
  small: number
  consecutivePairs: number
}

export type OtherGameConstraints = {
  required: number[]
  excluded: number[]
  oddCount?: number
  bigCount?: number
  sumMin?: number
  sumMax?: number
  spanMin?: number
  spanMax?: number
  maxConsecutivePairs?: number
}

export type OtherGenerationOptions = {
  happyPickCount?: number
  fc3dMode?: Fc3dMode
  mode?: OtherGenerationMode
  constraints?: OtherGameConstraints
  dan?: number[]
  tuo?: number[]
  multiple?: number[]
}

export type OtherGenerationResult = {
  tickets: OtherGameTicket[]
  warning?: string
  total?: number
}

export const EMPTY_OTHER_CONSTRAINTS: OtherGameConstraints = {
  required: [],
  excluded: [],
}

export const MAX_OTHER_EXPANDED_TICKETS = 1000

export const WELFARE_GAMES: WelfareGame[] = [
  {
    id: 'ssq',
    name: '双色球',
    shortName: '双色球',
    note: '6 红 + 1 蓝',
    rule: '红球 01–33 选 6，蓝球 01–16 选 1',
  },
  {
    id: 'qlc',
    name: '七乐彩',
    shortName: '七乐彩',
    note: '30 选 7',
    rule: '01–30 中选择 7 个号码',
  },
  {
    id: 'fc3d',
    name: '福彩3D',
    shortName: '3D',
    note: '三位数字',
    rule: '每位从 0–9 中选择一个数字',
  },
  {
    id: 'kl8',
    name: '快乐8',
    shortName: '快乐8',
    note: '80 选 1–10',
    rule: '01–80 中选择 1–10 个号码',
  },
]

function randomIndex(maxExclusive: number): number {
  if (maxExclusive <= 1) return 0
  const range = 0x100000000
  const limit = range - (range % maxExclusive)
  const buffer = new Uint32Array(1)
  let value = range
  while (value >= limit) {
    crypto.getRandomValues(buffer)
    value = buffer[0]
  }
  return value % maxExclusive
}

function uniqueSorted(values: readonly number[]): number[] {
  return [...new Set(values)].sort((a, b) => a - b)
}

function gameRange(game: Exclude<WelfareGameId, 'ssq'>): { min: number; max: number; bigFrom: number } {
  if (game === 'fc3d') return { min: 0, max: 9, bigFrom: 5 }
  if (game === 'qlc') return { min: 1, max: 30, bigFrom: 16 }
  return { min: 1, max: 80, bigFrom: 41 }
}

function pickCountFor(game: Exclude<WelfareGameId, 'ssq'>, happyPickCount = 10): number {
  if (game === 'qlc') return 7
  if (game === 'fc3d') return 3
  return Math.min(10, Math.max(1, Math.trunc(happyPickCount) || 1))
}

function countConsecutivePairs(values: readonly number[]): number {
  const unique = uniqueSorted(values)
  let pairs = 0
  for (let i = 1; i < unique.length; i++) {
    if (unique[i] - unique[i - 1] === 1) pairs++
  }
  return pairs
}

export function analyzeOtherNumbers(
  game: Exclude<WelfareGameId, 'ssq'>,
  numbers: readonly number[],
): OtherNumberMetrics {
  const { bigFrom } = gameRange(game)
  const sum = numbers.reduce((total, value) => total + value, 0)
  const odd = numbers.filter(value => value % 2 !== 0).length
  const big = numbers.filter(value => value >= bigFrom).length
  const min = Math.min(...numbers)
  const max = Math.max(...numbers)

  return {
    sum,
    span: numbers.length ? max - min : 0,
    odd,
    even: numbers.length - odd,
    big,
    small: numbers.length - big,
    consecutivePairs: countConsecutivePairs(numbers),
  }
}

function isPlainConstraints(constraints: OtherGameConstraints): boolean {
  return (
    constraints.required.length === 0 &&
    constraints.excluded.length === 0 &&
    constraints.oddCount === undefined &&
    constraints.bigCount === undefined &&
    constraints.sumMin === undefined &&
    constraints.sumMax === undefined &&
    constraints.spanMin === undefined &&
    constraints.spanMax === undefined &&
    constraints.maxConsecutivePairs === undefined
  )
}

function matchesConstraints(
  game: Exclude<WelfareGameId, 'ssq'>,
  numbers: readonly number[],
  constraints: OtherGameConstraints,
): boolean {
  const metrics = analyzeOtherNumbers(game, numbers)

  if (constraints.required.some(value => !numbers.includes(value))) return false
  if (constraints.excluded.some(value => numbers.includes(value))) return false
  if (constraints.oddCount !== undefined && metrics.odd !== constraints.oddCount) return false
  if (constraints.bigCount !== undefined && metrics.big !== constraints.bigCount) return false
  if (constraints.sumMin !== undefined && metrics.sum < constraints.sumMin) return false
  if (constraints.sumMax !== undefined && metrics.sum > constraints.sumMax) return false
  if (constraints.spanMin !== undefined && metrics.span < constraints.spanMin) return false
  if (constraints.spanMax !== undefined && metrics.span > constraints.spanMax) return false
  if (
    constraints.maxConsecutivePairs !== undefined &&
    metrics.consecutivePairs > constraints.maxConsecutivePairs
  ) return false

  return true
}

function validateConstraints(
  game: Exclude<WelfareGameId, 'ssq'>,
  pickCount: number,
  constraints: OtherGameConstraints,
  fc3dMode: Fc3dMode,
): string | null {
  const { min, max } = gameRange(game)
  const required = uniqueSorted(constraints.required)
  const excluded = uniqueSorted(constraints.excluded)

  if (required.some(value => value < min || value > max)) return '必选号码超出当前玩法范围。'
  if (excluded.some(value => value < min || value > max)) return '排除号码超出当前玩法范围。'
  if (required.some(value => excluded.includes(value))) return '同一个号码不能同时设为必选和排除。'

  if (constraints.sumMin !== undefined && constraints.sumMax !== undefined && constraints.sumMin > constraints.sumMax) {
    return '和值下限不能大于上限。'
  }
  if (constraints.spanMin !== undefined && constraints.spanMax !== undefined && constraints.spanMin > constraints.spanMax) {
    return '跨度下限不能大于上限。'
  }
  if (constraints.oddCount !== undefined && (constraints.oddCount < 0 || constraints.oddCount > pickCount)) {
    return '奇数个数超出当前玩法范围。'
  }
  if (constraints.bigCount !== undefined && (constraints.bigCount < 0 || constraints.bigCount > pickCount)) {
    return '大号个数超出当前玩法范围。'
  }

  const availableCount = max - min + 1 - excluded.length

  if (game === 'fc3d') {
    const distinctNeeded = fc3dMode === 'group6' ? 3 : fc3dMode === 'group3' ? 2 : 1
    if (availableCount < distinctNeeded) return '排除数字过多，无法组成当前 3D 投注方式。'
    const maxRequired = fc3dMode === 'group3' ? 2 : 3
    if (required.length > maxRequired) return `当前 3D 投注方式最多包含 ${maxRequired} 个不同的必选数字。`
    return null
  }

  if (required.length > pickCount) return `必选号码不能超过 ${pickCount} 个。`
  if (availableCount < pickCount) return '排除号码过多，剩余号码不足以生成一注。'
  return null
}

function uniqueBatch(
  count: number,
  factory: () => OtherGameTicket,
  keyOf: (ticket: OtherGameTicket) => string,
  predicate: (ticket: OtherGameTicket) => boolean = () => true,
  theoreticalMax?: number,
): OtherGenerationResult {
  const target = theoreticalMax === undefined ? count : Math.min(count, theoreticalMax)
  const tickets: OtherGameTicket[] = []
  const seen = new Set<string>()
  const maxAttempts = Math.min(100000, Math.max(8000, target * 1200))

  for (let attempts = 0; tickets.length < target && attempts < maxAttempts; attempts++) {
    const ticket = factory()
    const key = keyOf(ticket)
    if (seen.has(key) || !predicate(ticket)) continue
    seen.add(key)
    tickets.push(ticket)
  }

  let warning: string | undefined
  if (theoreticalMax !== undefined && count > theoreticalMax) {
    warning = `该设置最多只有 ${theoreticalMax} 个不重复组合，本次已生成全部可用组合。`
  } else if (tickets.length < count) {
    warning = `当前条件较严格，仅找到 ${tickets.length} 注不重复组合。可以适当放宽条件。`
  }

  return { tickets, warning, total: theoreticalMax }
}

function buildUniqueTicket(
  game: 'qlc' | 'kl8',
  pickCount: number,
  constraints: OtherGameConstraints,
): OtherGameTicket {
  const { max } = gameRange(game)
  const required = uniqueSorted(constraints.required)
  const excluded = new Set(constraints.excluded)
  const candidates = Array.from({ length: max }, (_, index) => index + 1)
    .filter(value => !excluded.has(value) && !required.includes(value))
  const numbers = [...required, ...secureShuffle(candidates).slice(0, pickCount - required.length)]
    .sort((a, b) => a - b)
  return { game, numbers }
}

function buildFc3dTicket(mode: Fc3dMode, excluded: readonly number[]): OtherGameTicket {
  const allowed = Array.from({ length: 10 }, (_, value) => value).filter(value => !excluded.includes(value))

  if (mode === 'direct') {
    return {
      game: 'fc3d',
      numbers: [
        allowed[randomIndex(allowed.length)],
        allowed[randomIndex(allowed.length)],
        allowed[randomIndex(allowed.length)],
      ],
    }
  }

  if (mode === 'group3') {
    const repeated = allowed[randomIndex(allowed.length)]
    const singlePool = allowed.filter(value => value !== repeated)
    const single = singlePool[randomIndex(singlePool.length)]
    return { game: 'fc3d', numbers: [repeated, repeated, single].sort((a, b) => a - b) }
  }

  return {
    game: 'fc3d',
    numbers: secureShuffle(allowed).slice(0, 3).sort((a, b) => a - b),
  }
}

function combinationsLimited<T>(values: readonly T[], choose: number, limit: number): T[][] {
  if (choose < 0 || choose > values.length || limit <= 0) return []
  if (choose === 0) return [[]]

  const result: T[][] = []
  const picked: T[] = []

  const walk = (start: number) => {
    if (result.length >= limit) return
    if (picked.length === choose) {
      result.push([...picked])
      return
    }

    const remaining = choose - picked.length
    for (let index = start; index <= values.length - remaining; index++) {
      picked.push(values[index])
      walk(index + 1)
      picked.pop()
      if (result.length >= limit) return
    }
  }

  walk(0)
  return result
}

export function calculateOtherDantuoCount(pickCount: number, dan: readonly number[], tuo: readonly number[]): number {
  const uniqueDan = uniqueSorted(dan)
  const uniqueTuo = uniqueSorted(tuo).filter(value => !uniqueDan.includes(value))
  if (pickCount <= 1 || uniqueDan.length < 1 || uniqueDan.length >= pickCount) return 0
  const need = pickCount - uniqueDan.length
  if (uniqueTuo.length < need) return 0
  return combinationCount(uniqueTuo.length, need)
}

export function calculateOtherMultipleCount(pickCount: number, pool: readonly number[]): number {
  const uniquePool = uniqueSorted(pool)
  if (uniquePool.length < pickCount) return 0
  return combinationCount(uniquePool.length, pickCount)
}

function expandDantuo(
  game: 'qlc' | 'kl8',
  pickCount: number,
  dan: readonly number[],
  tuo: readonly number[],
): OtherGenerationResult {
  const { min, max } = gameRange(game)
  const uniqueDan = uniqueSorted(dan).filter(value => value >= min && value <= max)
  const uniqueTuo = uniqueSorted(tuo)
    .filter(value => value >= min && value <= max && !uniqueDan.includes(value))

  if (pickCount <= 1) return { tickets: [], warning: '当前玩法至少需要选择 2 个号码才可使用胆拖展开。' }
  if (uniqueDan.length < 1 || uniqueDan.length >= pickCount) {
    return { tickets: [], warning: `胆码需要选择 1–${pickCount - 1} 个。` }
  }

  const need = pickCount - uniqueDan.length
  if (uniqueTuo.length < need) return { tickets: [], warning: `至少还需要 ${need} 个拖码。` }

  const total = combinationCount(uniqueTuo.length, need)
  const combos = combinationsLimited(uniqueTuo, need, MAX_OTHER_EXPANDED_TICKETS)
  return {
    tickets: combos.map(combo => ({
      game,
      numbers: [...uniqueDan, ...combo].sort((a, b) => a - b),
    })),
    total,
    warning: total > MAX_OTHER_EXPANDED_TICKETS
      ? `共有 ${total} 注，页面仅展开前 ${MAX_OTHER_EXPANDED_TICKETS} 注。`
      : undefined,
  }
}

function expandMultiple(
  game: 'qlc' | 'kl8',
  pickCount: number,
  pool: readonly number[],
): OtherGenerationResult {
  const { min, max } = gameRange(game)
  const uniquePool = uniqueSorted(pool).filter(value => value >= min && value <= max)

  if (uniquePool.length < pickCount) {
    return { tickets: [], warning: `组合展开至少需要选择 ${pickCount} 个号码。` }
  }

  const total = combinationCount(uniquePool.length, pickCount)
  const combos = combinationsLimited(uniquePool, pickCount, MAX_OTHER_EXPANDED_TICKETS)
  return {
    tickets: combos.map(numbers => ({ game, numbers: [...numbers] })),
    total,
    warning: total > MAX_OTHER_EXPANDED_TICKETS
      ? `共有 ${total} 注，页面仅展开前 ${MAX_OTHER_EXPANDED_TICKETS} 注。`
      : undefined,
  }
}

export function generateOtherGame(
  game: Exclude<WelfareGameId, 'ssq'>,
  count: number,
  options: OtherGenerationOptions = {},
): OtherGenerationResult {
  const safeCount = Math.min(200, Math.max(1, Math.trunc(count) || 1))
  const pickCount = pickCountFor(game, options.happyPickCount)
  const mode = options.mode ?? 'quick'
  const fc3dMode = options.fc3dMode ?? 'direct'
  const constraints = options.constraints ?? EMPTY_OTHER_CONSTRAINTS

  if (game !== 'fc3d' && mode === 'dantuo') {
    return expandDantuo(game, pickCount, options.dan ?? [], options.tuo ?? [])
  }

  if (game !== 'fc3d' && mode === 'multiple') {
    return expandMultiple(game, pickCount, options.multiple ?? [])
  }

  const effectiveConstraints = mode === 'conditional' ? constraints : EMPTY_OTHER_CONSTRAINTS
  const error = validateConstraints(game, pickCount, effectiveConstraints, fc3dMode)
  if (error) return { tickets: [], warning: error }

  if (game === 'qlc' || game === 'kl8') {
    const { max } = gameRange(game)
    const required = uniqueSorted(effectiveConstraints.required)
    const excluded = new Set(effectiveConstraints.excluded)
    const freeCount = Array.from({ length: max }, (_, index) => index + 1)
      .filter(value => !excluded.has(value) && !required.includes(value))
      .length
    const theoreticalMax = combinationCount(freeCount, pickCount - required.length)

    return uniqueBatch(
      safeCount,
      () => buildUniqueTicket(game, pickCount, effectiveConstraints),
      ticket => ticket.numbers.join('-'),
      ticket => matchesConstraints(game, ticket.numbers, effectiveConstraints),
      theoreticalMax,
    )
  }

  const allowedCount = 10 - uniqueSorted(effectiveConstraints.excluded).length
  const theoreticalMax = isPlainConstraints(effectiveConstraints)
    ? fc3dMode === 'direct'
      ? allowedCount ** 3
      : fc3dMode === 'group3'
        ? allowedCount * Math.max(0, allowedCount - 1)
        : combinationCount(allowedCount, 3)
    : undefined

  return uniqueBatch(
    safeCount,
    () => buildFc3dTicket(fc3dMode, effectiveConstraints.excluded),
    ticket => ticket.numbers.join(fc3dMode === 'direct' ? '' : '-'),
    ticket => matchesConstraints('fc3d', ticket.numbers, effectiveConstraints),
    theoreticalMax,
  )
}

export function otherTicketKey(ticket: OtherGameTicket): string {
  return `${ticket.game}|${ticket.numbers.join('-')}`
}

export function otherTicketsText(tickets: OtherGameTicket[]): string {
  return tickets
    .map((ticket, index) => {
      const values = ticket.game === 'fc3d'
        ? ticket.numbers.join(' ')
        : ticket.numbers.map(value => String(value).padStart(2, '0')).join(' ')
      return `${String(index + 1).padStart(2, '0')}. ${values}`
    })
    .join('\n')
}
